import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useAuth } from "react-oidc-context";

import { useSettingsModal } from "@/contexts/SettingsModalContext";
import { cn } from "@/lib/utils";
import { getDisplayName, getInitials } from "@/lib/userDisplay";
import { UserPanelContent } from "./UserPanelContent";

interface CollapsedUserAvatarProps {
  onTooltip?: (label: string, el: HTMLElement) => void;
  onTooltipHide?: () => void;
  variant?: "default" | "ghost";
}

export function CollapsedUserAvatar({
  onTooltip,
  onTooltipHide,
  variant = "default",
}: CollapsedUserAvatarProps) {
  const auth = useAuth();
  const { openSettingsModal } = useSettingsModal();
  const profile = auth.user?.profile;

  const name = getDisplayName(profile);
  const initials = getInitials(profile);

  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  const openSettings = () => {
    setOpen(false);
    openSettingsModal();
  };

  return (
    <>
      <DropdownMenu.Root modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            onMouseEnter={(event) => onTooltip?.(name, event.currentTarget)}
            onMouseLeave={() => onTooltipHide?.()}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full",
              "text-xs font-semibold",
              variant === "ghost"
                ? "border border-white/25 bg-white/5 text-white/90 transition-colors hover:border-white/40 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/25"
                : "bg-surface-elevated text-secondary transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-brand-cyan/30",
            )}
            aria-label="User menu"
          >
            {initials}
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="right"
            align="end"
            sideOffset={10}
            collisionPadding={12}
            className={cn(
              "z-50 outline-none",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=right]:slide-in-from-left-1",
              "data-[side=left]:slide-in-from-right-1",
              "data-[side=top]:slide-in-from-bottom-1",
              "data-[side=bottom]:slide-in-from-top-1",
            )}
          >
            <UserPanelContent onClose={close} onOpenSettings={openSettings} />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
}
